/* eslint-disable no-console */
import axios from "axios";
import { PATH_API } from "constants/api";
import { CACHE_QUERY_PARAM } from "constants/serviceWorker";
import { iPlanetsAuth, iPlanetsCookies } from "interfaces/models";
import { ApiResponse } from "interfaces/models/api";
import { User } from "interfaces/models/user";
import { axiosECS } from "services/baseAxios";
import { addParamToUrl } from "utils/fetch";

interface GetTokenRes {
  success: boolean;
  cookies: iPlanetsCookies;
}

const planetAuthPathV1 = `${process.env.REACT_APP_PLANETS_AUTH_HOST_URL}/v1`;

export const getForgeToken = async (
  shouldCache?: boolean
): Promise<{
  access_token: string;
  expires_in: number;
  token_type: string;
  expires_at?: number;
  shouldCache?: boolean;
}> => {
  return axiosECS.get(`${PATH_API.AUTH}/aps-token`, {
    params: { shouldCache },
  });
};

export const getToken = async (
  shouldCache?: boolean
): Promise<ApiResponse<GetTokenRes>> => {
  const { data } = await axios.get(
    process.env.REACT_APP_API_LOCAL_TOKEN || // use for run local server
      `${process.env.REACT_APP_API_HOST_URL}/v1/auth/token`,
    { withCredentials: true, params: { shouldCache } }
  );

  return data;
};

export const getCurrentUser = async (
  shouldCache?: boolean
): Promise<
  ApiResponse<{
    user: User;
    tokens: {
      expIn: string;
      idToken: string;
      refreshToken: string;
    };
  }>
> => {
  return axiosECS.get(`${PATH_API.AUTH}/current-user`, {
    params: {
      shouldCache,
    },
  });
};

export const logoutFromPlanet = async () => {
  const urlToken = `${planetAuthPathV1}/signOut`;
  await fetch(urlToken, {
    method: "post",
    credentials: "include",
  });

  return;
};

export const refreshTokenFromPlanet = async (
  shouldCache = false
): Promise<iPlanetsAuth | undefined> => {
  let urlRefreshToken = `${planetAuthPathV1}/refreshToken`;
  if (shouldCache) {
    urlRefreshToken = addParamToUrl(urlRefreshToken, CACHE_QUERY_PARAM, "true");
  }
  const MAX_RETRIES = 5;
  const DELAY_INCREMENT = 500;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const req = await fetch(urlRefreshToken, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (req.status !== 200) {
        return;
      }

      const data = await req.json();

      return data as iPlanetsAuth;
    } catch (error) {
      // we will retry in case response is undefined due to any reason
      const currentAttempt = attempt + 1;
      const remainingAttempts = MAX_RETRIES - currentAttempt;

      if (remainingAttempts === 0) {
        console.log("All refresh token attempts failed:", {
          totalAttempts: MAX_RETRIES,
          finalError: error,
        });

        return;
      }

      const nextDelay = DELAY_INCREMENT * currentAttempt;
      console.log(
        `Refresh token retry ${currentAttempt}/${MAX_RETRIES} failed:`,
        {
          error,
          remainingAttempts,
          nextDelay,
        }
      );

      await new Promise((resolve) => setTimeout(resolve, nextDelay));
    }
  }

  return;
};
